import { useParams } from 'next/navigation';

import {
  getGivingChildGiftsPath,
  getWriteSponsorshipPath,
} from '@sola/my-sdk/constants.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { FlexibleContentProps } from '@sola/schema/lib/schema.ts';

import {
  type GiftEligibility,
  useGiftEligibility,
} from '../../utils/gift-availability/use-gift-eligibility.ts';

enum LinkIds {
  gift = '#gift',
  write = '#write',
}

const isGiftContentItem = (item: FlexibleContentProps) => {
  return item.primaryCTA?.link === LinkIds.gift;
};

const departureActionLinks: Record<
  string,
  (beneficiaryGlobalId: string) => string
> = {
  [LinkIds.gift]: (beneficiaryGlobalId) =>
    getGivingChildGiftsPath(beneficiaryGlobalId, GiftTypeEnum.Final),
  [LinkIds.write]: getWriteSponsorshipPath,
};

export const resolveDepartureOverviewContent = (
  content: FlexibleContentProps | undefined,
  beneficiaryGlobalId: string | undefined,
  giftEligibility: Pick<GiftEligibility, 'isPending' | 'isFinalGiftEligible'>,
): FlexibleContentProps | undefined => {
  if (!content) {
    return;
  }

  return {
    ...content,
    primaryCTA: undefined,
    secondaryCTA: undefined,
    tertiaryCTA: undefined,
    items: content.items
      // InfoCardMain requires 2 items - if we filter down to 1, then it includes a fake "Placeholder"
      ?.filter((item) => {
        if (!isGiftContentItem(item)) {
          return true;
        }

        if (giftEligibility.isPending) {
          return false;
        }

        return giftEligibility.isFinalGiftEligible(beneficiaryGlobalId);
      })
      ?.map((item) => {
        const primaryCTA = item.primaryCTA;
        const resolveLink = primaryCTA
          ? departureActionLinks[primaryCTA.link]
          : undefined;

        if (!primaryCTA || !resolveLink || !beneficiaryGlobalId) {
          return item;
        }

        return {
          ...item,
          primaryCTA: {
            ...primaryCTA,
            link: resolveLink(beneficiaryGlobalId),
          },
        };
      }),
  };
};

export const useDepartureOverview = (
  content: FlexibleContentProps | undefined,
) => {
  const params = useParams<{ globalId: string }>();
  const giftEligibility = useGiftEligibility();
  const { globalId } = params ?? {};

  return resolveDepartureOverviewContent(content, globalId, giftEligibility);
};
